import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Kicker, RolePill } from "@/components/common";
import { LogOut, Mail, User as UserIcon, Building2 } from "lucide-react";

export default function Profile() {
  const { user, organization, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;

  const initials = (user.name || user.email)
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 animate-fade-up">
      <Kicker>Profile</Kicker>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1 mb-6">Your account</h1>

      <div className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-zinc-950 text-white flex items-center justify-center font-display text-xl font-semibold">
            {initials}
          </div>
          <div>
            <div className="font-display text-xl font-semibold text-zinc-950" data-testid="profile-name">{user.name}</div>
            <div className="mt-1 flex items-center gap-2">
              <RolePill role={user.role} />
              <span className="text-xs text-zinc-500">· member since {new Date(user.created_at).getFullYear()}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-zinc-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field icon={UserIcon} label="Name" value={user.name} testId="profile-field-name" />
          <Field icon={Mail} label="Email" value={user.email} testId="profile-field-email" />
          {organization ? (
            <Field
              icon={Building2}
              label="Organization"
              value={`${organization.name} · ${organization.domain}`}
              testId="profile-field-org"
            />
          ) : null}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-6 flex items-center justify-between">
        <div>
          <div className="font-medium text-zinc-950">Sign out</div>
          <p className="mt-1 text-sm text-zinc-500">End your session and return to the sign-in screen.</p>
        </div>
        <button
          data-testid="profile-signout-btn"
          onClick={() => {
            logout();
            nav("/login", { replace: true });
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm hover:bg-zinc-50"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-zinc-100 bg-zinc-50 p-4">
      <div className="flex items-center gap-1.5 label-kicker"><Icon className="w-3 h-3" /> {label}</div>
      <div className="mt-1.5 text-sm text-zinc-900">{value}</div>
    </div>
  );
}
